import {
  archiveEmail,
  batchArchiveEmails,
  bookingEmailLabel,
  getMonitoring,
  getPaginated,
} from "@/services/ai";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import moment from "moment";
import { Archive, Loader2 } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/context/AuthContext";

type bookingErrorType =
  | "missingAlias"
  | "creationDate"
  | "cancellation"
  | "checkInOut"
  | "refMissing"
  | "priceMismatch"
  | "tooLong"
  | "late"
  | "unknown";

type bookingEmailStatus = "pending" | "success" | "error";

export const parsedContentSchema = z.object({
  _createdAt: z.string(),
  collectionId: z.string(),
  lang: z.string(),
  owner: z.string(),
  originalPrice: z.number(),
  lockDate: z.string(),
  guests: z
    .object({
      firstName: z.string(),
      secondName: z.string(),
      email: z.string(),
    })
    .array(),
  payload: z.object({
    reference: z.string(),
    checkin: z.string(),
    checkout: z.string(),
    board: z.string().optional(),
    rooms: z
      .object({
        name: z.string(),
        guestsAdults: z.number(),
        guestsKids: z.number(),
        amenities: z.string(),
      })
      .array(),

    extra: z.string().optional(),
  }),
  bookedWithLoyaltyCode: z.string().optional(),
  reservationType: z.string(),
});

export type bookingEmailType = {
  _id: string;
  _createdAt: Date;
  emailId: string;
  encryptedContent: { headers: { subject: string }; text: string };
  hashedContent: string;
  step: number;
  status: bookingEmailStatus;
  errorType?: bookingErrorType;
  hotelAlias: string;
  labels: bookingEmailLabel[];
  encryptedParsedContent:
    | z.infer<typeof parsedContentSchema>
    | "No data"
    | "Ofuscated data";
  expiresAt: Date;
};

const Ai = () => {
  const [monitoring, setMonitoring] = useState<{
    total: number;
    success: number;
    pending: number;
    error: number;
    errorTypes: Record<bookingErrorType, number>;
  }>();
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringError, setMonitoringError] = useState(false);

  const [emails, setEmails] = useState<bookingEmailType[]>();
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsPage, setEmailsPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [numberOfDays, setNumberOfDays] = useState(2);

  const [errorTypes, setErrorTypes] = useState<bookingErrorType[]>([]);

  const { userId } = useAuthContext();

  const [shiftHeld, setShiftHeld] = useState(false);

  function downHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(true);
    }
  }

  function upHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(false);
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  }, []);

  const fetchMonitoring = async () => {
    setMonitoringLoading(true);
    try {
      const res = await getMonitoring({
        startDate: new Date(
          new Date().getTime() - numberOfDays * 24 * 60 * 60 * 1000
        ),
      });
      setMonitoring(res);
    } catch {
      setMonitoring(undefined);
      setMonitoringError(true);
    }
    setMonitoringLoading(false);
  };

  const fetchEmails = async (page: number, eTypes: string[]) => {
    setEmailsLoading(true);
    try {
      const res = await getPaginated({
        page: page,
        size: 10,
        startDate: new Date(
          new Date().getTime() - numberOfDays * 24 * 60 * 60 * 1000
        ),
        errorTypes: eTypes,
        labels: ["inbox"],
      });
      setEmails(res.data);
      setEmailsPage(Math.ceil(res.page));
      setTotalPages(Math.ceil(res.total / res.size));
    } catch {
      setEmails(undefined);
    } finally {
      setSelectedIds([]);
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchMonitoring();
    }
  }, [userId]);

  useEffect(() => {
    fetchEmails(emailsPage, errorTypes);
  }, [emailsPage, errorTypes]);

  if (emailsLoading) {
    return (
      <div className="flex flex-col justify-center items-center w-full h-full gap-6">
        <p>Sto raccogliendo i dati</p>
        <Loader2 className="mr-2 h-12 w-12 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12">
      <div className="flex items-center w-full justify-between">
        <div className="flex items-center gap-4">
          <input
            value={numberOfDays}
            onChange={(e) => {
              const days = parseInt(e.target.value);
              setNumberOfDays(days);
            }}
            type="number"
            step={1}
            min={1}
            className="border-takyon-black border rounded-lg p-2"
          />
          <p>Giorni</p>
          <Button
            variant="takyon"
            onClick={() => {
              fetchMonitoring();
              fetchEmails(emailsPage, errorTypes);
            }}
          >
            Aggiorna
          </Button>
        </div>
        <div>
          <Link to="/admin/archived_ai">Vedi archivio</Link>
        </div>
      </div>
      {monitoringLoading ? (
        "Loading..."
      ) : monitoring ? (
        <>
          <div className="flex flex-wrap gap-4">
            {(Object.keys(monitoring) as (keyof typeof monitoring)[]).map(
              (key, i) => {
                if (key === "errorTypes") {
                  return null;
                }
                return (
                  <div key={i} className="p-4 shadow-lg rounded-xl">
                    <p>{key}</p>
                    <p>{monitoring[key]}</p>
                  </div>
                );
              }
            )}

            <div
              className="p-4 shadow-lg rounded-xl"
              style={{
                backgroundColor:
                  monitoring.error / (monitoring.total - monitoring.pending) <=
                  0.02
                    ? "#9AFEE3"
                    : "#ff746c",
              }}
            >
              <p>failure rate</p>
              <p>
                {(
                  (monitoring.error / (monitoring.total - monitoring.pending)) *
                  100
                ).toFixed(2)}
                %
              </p>
            </div>
            <div
              className="p-4 shadow-lg rounded-xl"
              style={{
                backgroundColor:
                  monitoring.success /
                    (monitoring.total - monitoring.pending) >=
                  0.98
                    ? "#9AFEE3"
                    : "#ff746c",
              }}
            >
              <p>success rate</p>
              <p>
                {(
                  (monitoring.success /
                    (monitoring.total - monitoring.pending)) *
                  100
                ).toFixed(2)}
                %
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {(
              Object.keys(
                monitoring.errorTypes
              ) as (keyof typeof monitoring.errorTypes)[]
            ).map((key, i) => (
              <button
                className="px-2 shadow-lg rounded-lg"
                style={{
                  backgroundColor: errorTypes.includes(key) ? "gray" : "",
                  color: errorTypes.includes(key) ? "white" : "",
                }}
                key={i}
                onClick={() => {
                  setErrorTypes((p) => {
                    if (p.includes(key)) {
                      return p.filter((e) => e !== key);
                    }
                    return [...p, key];
                  });
                }}
              >
                <p>
                  {key}: {monitoring.errorTypes[key]}
                </p>
              </button>
            ))}
          </div>
        </>
      ) : monitoringError ? (
        ""
      ) : (
        ""
      )}

      {emails && emails.length > 0 ? (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex gap-6 items-center">
              <div className="flex gap-2 items-center">
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  checked={selectedIds.length === emails.length}
                  onChange={(e) => {
                    if (!emails) return;
                    if (e.target.checked) {
                      setSelectedIds(emails.map((email) => email._id));
                      return;
                    }
                    setSelectedIds([]);
                  }}
                />
                <p>Seleziona tutti</p>
              </div>

              <button
                className="flex gap-2 items-center border shadow-lg rounded-2xl px-2 py-1"
                onClick={async (event) => {
                  event.stopPropagation();
                  if (confirm("Clicca conferma per archiviare i selezionati")) {
                    await batchArchiveEmails(selectedIds);
                    fetchEmails(emailsPage, errorTypes);
                  }
                }}
                style={{
                  opacity: selectedIds.length === 0 ? 0.5 : 1,
                  cursor: selectedIds.length === 0 ? "unset" : "pointer",
                }}
                disabled={selectedIds.length === 0}
              >
                <Archive className="h-4 w-4" />
                <p>Archiva selezionati</p>
              </button>
            </div>
            {emails.map((email, index) => (
              <div className="p-2 shadow-lg rounded-lg cursor-pointer flex items-center justify-between gap-2">
                <div className="flex gap-2">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={selectedIds.includes(email._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds((p) => {
                          const candidates: string[] = [];
                          let isBroken = false;
                          if (shiftHeld) {
                            for (let i = index - 1; i >= 0; i--) {
                              if (p.includes(emails[i]._id)) {
                                isBroken = true;
                                break;
                              }
                              candidates.push(emails[i]._id);
                            }
                          }
                          return [
                            ...p,
                            ...(isBroken ? candidates : []),
                            email._id,
                          ];
                        });
                        return;
                      }
                      if (!shiftHeld) {
                        setSelectedIds((p) =>
                          p.filter((id) => id !== email._id)
                        );
                      }
                    }}
                  />
                  <Link
                    className="flex items-center gap-2"
                    to={`/admin/ai/${email._id}`}
                    target="_blank"
                  >
                    <div
                      className="px-2 py-1 rounded-lg"
                      style={{
                        backgroundColor:
                          email.status === "success"
                            ? "#9AFEE3"
                            : email.status === "error"
                            ? "#ff746c"
                            : "#ddd",
                      }}
                    >
                      <p>
                        #
                        {email.status === "error"
                          ? email.errorType
                          : email.status}
                      </p>
                    </div>
                    <p>{email.encryptedContent.headers.subject}</p>
                  </Link>
                </div>
                <div className="flex gap-4">
                  <p className="text-xs text-gray-500">
                    {moment(email._createdAt).format("DD/MM/YYYY hh:mm:ss")}
                  </p>
                  <button
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (confirm("Clicca conferma per archiviare")) {
                        await archiveEmail(email._id);
                        fetchEmails(emailsPage, errorTypes);
                        return;
                      }
                    }}
                    className="z-10"
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={() => setEmailsPage((p) => Math.max(p - 1, 1))}
                />
              </PaginationItem>

              {Array.from({ length: totalPages }, (_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    href="#"
                    isActive={emailsPage === i + 1}
                    onClick={() => setEmailsPage(i + 1)}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={() =>
                    setEmailsPage((p) => Math.min(p + 1, totalPages))
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </>
      ) : null}
    </div>
  );
};

export default Ai;
